"use client";

import {
  ColumnDef,
  ColumnFiltersState,
  ColumnOrderState,
  PaginationState,
  Row,
  SortingState,
  Table,
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { DndProvider } from "react-dnd";

import { HTML5Backend } from "react-dnd-html5-backend";
import TableHeader from "./Header";
import ColumnSetting from "./ColumnSetting";
import { Button } from "../ui/button";
import ExportBtn from "./ExportBtn";
import { CalcData, ParamsItem } from "./type";
import Pagination from "./Pagination";
import { useScopedI18n } from "@/messages/client";
import TableCell from "./Cell";

import { Loader2, SlidersHorizontal } from "lucide-react";
import FlexBottom from "./FlexBottom";
import SendEmail from "./SendEmail";
import AllFilter from "./AllFilter";
import Script from "next/script";

function getFacetedMinMaxValues<TData extends unknown>(): (
  table: Table<TData>,
  columnId: string
) => () => undefined | [number, number] {
  return (table: Table<TData>, columnId: string) => () => {
    const column = table.getColumn(columnId);

    const tableData = table.options.data as Record<string, any>[];

    const columnData = tableData.map((row) => row[columnId]) as ParamsItem[];

    const type = column?.columnDef?.filterFn as string;

    if (type === "calc") {
      const values = columnData.reduce((acc: number[], cur: ParamsItem) => {
        if (cur?.data?.value) {
          const value = cur.data.value as CalcData[];

          acc.push(...value.map((v) => Number(v.value)));
        }
        return acc;
      }, []);

      return [Math.min(...values), Math.max(...values)];
    }

    if (type === "range") {
      const values = columnData.reduce((acc: number[], cur: ParamsItem) => {
        if (cur?.data?.value) {
          const value = cur.data.value as number[][];

          acc.push(...value.flat());
        }
        return acc;
      }, []);

      return [Math.min(...values), Math.max(...values)];
    }
  };
}

export default function ProductFilter<T extends object>({
  data,
  columns,
  count,
  categorySlug,
  loading,
}: {
  data: T[];
  columns: (ColumnDef<T, any> & {
    accessorKey?: string;
  })[];
  count?: number;
  categorySlug?: string;
  defaultSort?: { id: string; desc: boolean }[];
  loading?: boolean;
}) {
  const t = useScopedI18n("选型器");

  const container = useRef<HTMLDivElement>(null);

  const columnWidthRef = useRef<Record<string, number>>({});

  const [allFilter, setAllFilter] = useState(false);

  // 控制列的显示隐藏
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({});

  // 控制列的筛选项
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  // 控制列的宽度
  const [columnWidth, setColumnWidth] = useState({});

  // 选择列
  const [rowSelection, setRowSelection] = useState({});

  const [sorting, setSorting] = useState<SortingState>([]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });

  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);

  const table = useReactTable({
    data,
    columns,

    state: {
      sorting,
      pagination,
      rowSelection,
      columnVisibility,
      columnOrder,
      columnFilters,
      columnSizing: columnWidth,
    },
    enableMultiSort: true,
    enableMultiRemove: true,
    filterFns: {
      select: (
        row: Row<T>,
        columnId: string,
        filterValue: (number | string)[]
      ) => {
        if (filterValue.length === 0) return true;

        const original = row.getValue(columnId) as ParamsItem;

        const value = original?.data?.value as number | string;

        return filterValue.indexOf(value) !== -1;
      },
      selects: (
        row: Row<T>,
        columnId: string,
        filterValue: (number | string)[]
      ) => {
        if (filterValue.length === 0) return true;

        const original = row.getValue(columnId) as ParamsItem;

        const values = (original?.data?.value as (number | string)[]) ?? [];

        const compare = Array.isArray(values) ? values : [values];
        return compare?.some?.((value) => filterValue.indexOf(value) !== -1);
      },
      text: (row: Row<T>, columnId: string, filterValue: string) => {
        const original = row.getValue(columnId) as ParamsItem;

        const value = (original?.data?.value as string) ?? "";

        // 忽略大小写
        return value?.toLowerCase()?.includes(filterValue.toLowerCase());
      },
      calc: (row: Row<T>, columnId: string, filterValue: [number, number]) => {
        if (!filterValue.length) return true;

        const original = row.getValue(columnId) as ParamsItem;

        const value = original?.data?.value as CalcData[];

        if (value) {
          for (let i = 0; i < value.length; i++) {
            const calcData = value[i];

            const num = Number(calcData.value);

            const symbol = calcData.symbol;

            if (
              symbol === ">=" &&
              num >= filterValue[0] &&
              num <= filterValue[1]
            ) {
              return true;
            }

            if (
              symbol === "<=" &&
              num <= filterValue[1] &&
              num >= filterValue[0]
            ) {
              return true;
            }

            if (
              symbol === ">" &&
              num > filterValue[0] &&
              num <= filterValue[1]
            ) {
              return true;
            }

            if (
              symbol === "<" &&
              num < filterValue[1] &&
              num >= filterValue[0]
            ) {
              return true;
            }

            if (
              symbol === "=" &&
              filterValue[0] <= num &&
              num <= filterValue[1]
            ) {
              return true;
            }
          }

          return false;
        }

        return false;
      },
      range: (row: Row<T>, columnId: string, filterValue: [number, number]) => {
        if (!filterValue.length) return true;

        const original = row.getValue(columnId) as ParamsItem;

        const value = original?.data?.value as number[][];

        if (value) {
          for (let i = 0; i < value.length; i++) {
            const numAry = value[i];

            const minNumber = Math.min(...numAry);

            const maxNumber = Math.max(...numAry);

            // 计算是否在范围内
            if (filterValue[0] >= maxNumber) {
              return false;
            }

            // 计算是否有交集
            if (filterValue[0] >= minNumber || filterValue[1] >= maxNumber) {
              return true;
            }

            continue;
          }

          return false;
        }

        return false;
      },
      product: (
        row: Row<T>,
        columnId: string,
        filterValue: { search: string; newProduct: boolean }
      ) => {
        const original = row.original as Record<string, any>;

        const { search, newProduct = false } = filterValue;

        const is_new_product = original.is_new_product ?? false;

        const value = row.getValue(columnId) as string;

        if (!search && newProduct) {
          return is_new_product;
        }

        if (search && newProduct) {
          return is_new_product && value.includes(search);
        }

        if (search && !newProduct) {
          return value.includes(search);
        }

        return true;
      },
    },
    sortingFns: {
      select: (a, b, columnId) => {
        const valueA = a.original[columnId]?.data?.value as number;
        const valueB = b.original[columnId]?.data?.value as number;

        return valueA - valueB;
      },
      selects: (a, b, columnId) => {
        const valueA = a.original[columnId]?.data?.value as number[];
        const valueB = b.original[columnId]?.data?.value as number[];

        return valueA?.length - valueB.length;
      },
      calc: (a, b, columnId) => {
        const valueA = a.original[columnId]?.data?.value as CalcData[];
        const valueB = b.original[columnId]?.data?.value as CalcData[];

        const numA = isNaN(Number(valueA?.[0]?.value))
          ? 0
          : Number(valueA[0].value);
        const numB = isNaN(Number(valueB?.[0]?.value))
          ? 0
          : Number(valueB[0].value);

        return numA - numB > 0 ? -1 : 1;
      },
    },
    columnResizeMode: "onChange",
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnWidth,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    onRowSelectionChange: setRowSelection,
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
  });

  const onAutoWidth = useCallback(() => {
    if (container.current) {
      // 固定列的宽度(物料号和图片列)
      const fixedWidth = 250 + 90;
      const spareWidth = container.current.clientWidth - fixedWidth;

      // 计算每列内容的最大宽度
      const contentWidths = columns.reduce<Record<string, number>>(
        (acc, column) => {
          if (
            column.accessorKey === "material_number" ||
            column.accessorKey === "images"
          ) {
            return acc;
          }

          // 获取列的所有单元格内容
          const cells = table.getRowModel().rows.map((row) => {
            const cell = row.getValue(
              column.accessorKey as string
            ) as ParamsItem;
            return cell?.data?.display?.toString() || "";
          });

          // 获取表头内容
          const headerContent = column.header?.toString() || "";

          // 创建临时 span 来测量文本宽度
          const span = document.createElement("span");
          span.style.visibility = "hidden";
          span.style.position = "absolute";
          span.style.whiteSpace = "nowrap";
          document.body.appendChild(span);

          // 计算表头宽度
          span.textContent = headerContent;
          const headerWidth = span.offsetWidth;

          // 计算内容最大宽度
          const maxContentWidth = Math.max(
            headerWidth,
            ...cells.map((content) => {
              span.textContent = content;
              return span.offsetWidth;
            })
          );

          // 清理临时元素
          document.body.removeChild(span);

          // 设置列宽度 (加上padding和border的宽度)
          acc[column.accessorKey as string] = Math.min(
            maxContentWidth + 24,
            300
          );
          return acc;
        },
        {}
      );

      // 计算所有列的总宽度
      const totalContentWidth = Object.values(contentWidths).reduce(
        (sum, width) => sum + width,
        0
      );

      // 如果总宽度小于可用空间，则按比例分配剩余空间
      if (totalContentWidth < spareWidth) {
        const ratio = spareWidth / totalContentWidth;
        Object.keys(contentWidths).forEach((key) => {
          contentWidths[key] = Math.floor(contentWidths[key] * ratio);
        });
      }

      columnWidthRef.current = contentWidths;
      setColumnWidth(contentWidths);
    }
  }, [columns, table]);

  useEffect(() => {
    setColumnOrder(columns?.map((column) => column.accessorKey as string));
  }, [columns]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      onAutoWidth();
    }

    window.addEventListener("resize", onAutoWidth);

    return () => {
      window.removeEventListener("resize", onAutoWidth);
    };
  }, [onAutoWidth, data]);

  useEffect(() => {
    if (container.current) {
      const newWidths = { ...columnWidthRef.current };

      const visibleColumns = Object.keys(newWidths).reduce<[string, boolean][]>(
        (acc, cur) => {
          const value = columnVisibility[cur];

          if (typeof value === "boolean") {
            acc.push([cur, value]);
          } else if (typeof value === "undefined") {
            acc.push([cur, true]);
          } else {
            acc.push([cur, false]);
          }

          return acc;
        },
        []
      );

      const spareWidth = container.current.clientWidth - (250 + 90);

      // 使用 columnWidthRef 中保存的原始宽度

      // 计算可见列的总宽度
      const totalVisibleWidth = visibleColumns.reduce((sum, [key, value]) => {
        if (value) {
          return sum + (newWidths[key] || 0);
        }

        return sum;
      }, 0);

      // 只有当总宽度小于可用空间时，才进行比例分配
      if (totalVisibleWidth < spareWidth) {
        const ratio = spareWidth / totalVisibleWidth;
        visibleColumns.forEach(([key, value]) => {
          if (value) {
            newWidths[key] = Math.floor((newWidths[key] || 0) * ratio);
          }
        });
      }
      // 否则保持原始宽度

      setColumnWidth(newWidths);
    }
  }, [columnVisibility]);

  useEffect(() => {
    if (!loading) {
      // @ts-ignore
      window?.syncscroll?.reset();
    }
  }, [loading]);

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        {loading ? (
          <div className="flex h-[200px] items-center justify-center">
            <Loader2 className="animate-spin text-primary" size={50} />
          </div>
        ) : (
          <div className="mt-5">
            <div className="mb-3 flex flex-col justify-between md:mb-5 md:flex-row md:items-center">
              <div className="flex flex-col space-y-5 md:flex-row md:items-center md:justify-start md:space-x-5 md:space-y-0">
                <Button
                  variant="activeOutline"
                  className="space-x-2 rounded-md"
                  onClick={() => {
                    setAllFilter(!allFilter);
                  }}>
                  <SlidersHorizontal size={16} />

                  <span>
                    {allFilter ? t("隐藏筛选条件") : t("全部筛选条件")}
                  </span>
                </Button>

                <ColumnSetting table={table} />

                <div className="mt-2 flex items-center space-x-5">
                  <Button
                    variant="link"
                    className="px-0 text-[#007c8c] marker:px-4"
                    onClick={() => {
                      table.resetColumnFilters(true);
                      onAutoWidth();
                      table.resetSorting(true);
                      table.resetRowSelection(true);
                      table.resetColumnVisibility(true);
                      table.resetPageIndex(true);
                    }}>
                    {t("重置表")}
                  </Button>

                  <p className="text-base text-[#26272A] md:block">
                    {t<any, any>("共计{current}/{count}个产品", {
                      count: count ?? 0,
                      current: table.getFilteredRowModel().rows.length,
                    })}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-5 pt-3 md:pt-0">
                <SendEmail />
                <ExportBtn table={table} />
              </div>
            </div>

            <div className="relative flex h-full w-full">
              {allFilter && <AllFilter table={table} />}

              <div
                style={{
                  width: allFilter ? "calc(100% - 304px)" : "100%",
                }}
                className="product_filter_last relative h-full w-full flex-1">
                <div
                  ref={container}
                  className="h-full w-full flex-1 border  border-[#ccc]">
                  <div className="relative h-full w-full">
                    <div className="sticky top-[62px] z-30 w-full">
                      <div
                        className="syncscroll app-table-scroll w-full bg-[#f8f8f8]"
                        // @ts-ignore
                        name="product_table">
                        <div
                          className="bg-[#f8f8f8]"
                          style={{ width: table.getTotalSize() }}>
                          <div className="whitespace-nowrap border-b border-[#ccc]">
                            {table.getHeaderGroups().map((headerGroup) => (
                              <div className="flex" key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                  <TableHeader
                                    key={header.id}
                                    header={header}
                                    table={table}
                                    categorySlug={categorySlug}
                                  />
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      className="syncscroll app-table-scroll w-full"
                      // @ts-ignore
                      name="product_table">
                      <div
                        className="relative h-full"
                        style={{ width: table.getTotalSize() }}>
                        {table.getRowModel().rows.map((row) => (
                          <div
                            className="flex border-b border-[#ccc] last:border-b-0"
                            key={row.id}>
                            {row.getVisibleCells().map((cell) => {
                              return <TableCell key={cell.id} cell={cell} />;
                            })}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="sticky bottom-0 z-30">
                      <div
                        className="syncscroll w-full overflow-x-auto"
                        // @ts-ignore
                        name="product_table">
                        <div
                          style={{
                            width: table.getTotalSize(),
                            height: "1px",
                            maxHeight: "1px",
                            minHeight: "1px",
                          }}></div>
                      </div>
                    </div>
                  </div>
                </div>

                <Pagination table={table} />
              </div>
            </div>
          </div>
        )}
      </DndProvider>
      <FlexBottom table={table} categorySlug={categorySlug} />

      <Script strategy="lazyOnload" src="/syncscroll.js" />
    </>
  );
}
